// PRD §15.5 — Conversation Detail analysis embed.
//
// Route: /embed/thread/:threadId/analysis?workflow_key=GEE
//
// Renders the most-recently committed analysis run for a thread, chrome
// stripped. iOS auto-sizes the WebView to our content height.

import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import EmbedLayout from "./EmbedLayout";
import ArcteryxAnalysisView from "../ArcteryxAnalysisView";
import CoachingReportView from "../CoachingReportView";
import GeeCoachingReport from "../GeeCoachingReport";
import {
  getCurrentCoachingReport,
  getLatestCommittedAnalysisRun,
  getThread,
} from "../../services/embed";

export default function EmbedAnalysisView() {
  const { threadId } = useParams();
  const [searchParams] = useSearchParams();
  const workflowKey = searchParams.get("workflow_key") || "GEE";

  const [run, setRun] = useState(null);
  const [currentCoachingReport, setCurrentCoachingReport] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCurrentCoachingReport(null);

    Promise.all([
      getLatestCommittedAnalysisRun(threadId, workflowKey),
      getThread(threadId),
      getCurrentCoachingReport(threadId).catch(() => null),
    ])
      .then(([runResponse, threadResponse, coachingResponse]) => {
        if (cancelled) return;
        // Response shape per PRD: `{ items: [...] }` (paginated list).
        // Tolerate either {items: [run]}, {runs: [run]}, or a bare run row.
        const items =
          runResponse?.items ||
          runResponse?.runs ||
          (Array.isArray(runResponse) ? runResponse : null) ||
          (runResponse ? [runResponse] : []);
        setRun(items[0] || null);
        setCurrentCoachingReport(coachingResponse || null);
        setThread(threadResponse || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Failed to load analysis.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [threadId, workflowKey]);

  return (
    <EmbedLayout>
      <Box w="100%" px={4} py={3} data-testid="embed-analysis-view">
        {loading && (
          <Center py={8}>
            <Spinner size="md" />
          </Center>
        )}

        {!loading && error && (
          <Text color="red.500" data-testid="embed-analysis-error">
            {error}
          </Text>
        )}

        {!loading && !error && !run && !currentCoachingReport && (
          <Text color="gray.500" data-testid="embed-analysis-empty">
            No analysis yet.
          </Text>
        )}

        {!loading && !error && currentCoachingReport && (
          <CoachingReportView
            analysis={currentCoachingReport}
            threadId={threadId}
            displayMode="embed"
          />
        )}

        {!loading && !error && run && !currentCoachingReport && (
          (() => {
            // The thread_analysis_runs row stores the analyzer JSON in
            // `analysis_json` on the backend; the API serialiser exposes it
            // as `analysis` historically (kept for backwards-compat with
            // ArcteryxAnalysisView). Tolerate either.
            const analysisJson =
              run.analysis_json ?? run.analysis ?? run.result ?? run;
            if (workflowKey === "GEE") {
              return (
                <GeeCoachingReport
                  analysis={analysisJson}
                  threadId={threadId}
                  displayMode="embed"
                />
              );
            }
            return (
              <ArcteryxAnalysisView
                conversationData={{
                  analysis: analysisJson,
                  thread,
                }}
              />
            );
          })()
        )}
      </Box>
    </EmbedLayout>
  );
}
